/**
 * Live Tier-1 widget state per record (spec-widget-mcp 05 Step 7, as re-pointed by WM04).
 *
 * Chat sends the widget its half on the ONE streaming transport, `record_update_stream`, with no
 * `chunk`/`done` (WM04; `conversation.widget` is the inbox's topic, not ours):
 *   - `widget`       the full stored envelope (`state`, `shownAt`, `submittedValues`, …) — first when it
 *                    is shown, again whenever its state moves (`submitted`, `expired`);
 *   - `widgetSubmit` `{ recordId, outcome:'rejected', code, fields? }` — a submission that started no turn.
 *
 * Neither is a message: the widget PATCHES the record it belongs to and never appends a bubble.
 * Chat.jsx's stream handler ignores both (they carry nothing `StreamingManager.ingest` understands),
 * and the record list it owns is never touched — the record's Receiver reads the live envelope from
 * here and prefers it over the persisted `reply.widget` unless that one is further along
 * (`resolveEnvelope`). History hydration is `reply.widget` itself.
 */
import eventEmitter from "../utils/eventEmitter";

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** shown < submitted = expired: state only moves forward (WM04). */
const STATE_RANK = { shown: 0, submitted: 1, expired: 1 };
const rank = (envelope) => STATE_RANK[envelope?.state] ?? 0;

/**
 * The envelope to render for a record: the persisted one, or the live one when it is at least as far
 * along (a live `submitted` beats a persisted `shown`; a reload's `submitted` beats nothing older).
 * @param {object|null|undefined} persisted `reply.widget`
 * @param {object|null|undefined} live the last `record_update_stream.widget`
 * @returns {object|null}
 */
export function resolveEnvelope(persisted, live) {
	const p = isPlainObject(persisted) ? persisted : null;
	const l = isPlainObject(live) ? live : null;
	if (!l) return p;
	if (!p) return l;
	return rank(l) >= rank(p) ? l : p;
}

export class WidgetEnvelopeStore {
	constructor(emitter = eventEmitter) {
		this.emitter = emitter;
		this.envelopes = {};
		this.outcomes = {};
		this.listeners = new Set();
		this.attached = false;
		this.seq = 0;
		this.handle = this.handle.bind(this);
	}

	/** Start listening to the socket topic (once, on the first subscriber). */
	attach() {
		if (this.attached) return;
		this.attached = true;
		this.emitter.on("record_update_stream", this.handle);
	}

	/**
	 * Fold one `record_update_stream` message. Returns whether it carried widget state.
	 * @param {{recordId?: string, widget?: object, widgetSubmit?: object}} message
	 */
	handle(message) {
		const { recordId, widget, widgetSubmit } = message || {};
		if (!recordId) return false;
		let changed = false;
		if (isPlainObject(widget)) {
			this.envelopes[recordId] = widget;
			changed = true;
		}
		if (isPlainObject(widgetSubmit) && typeof widgetSubmit.code === "string") {
			this.seq += 1;
			this.outcomes[recordId] = { ...widgetSubmit, seq: this.seq };
			changed = true;
		}
		if (changed) this.listeners.forEach((fn) => fn(recordId));
		return changed;
	}

	/** @param {(recordId: string) => void} fn @returns {() => void} unsubscribe */
	subscribe(fn) {
		this.attach();
		this.listeners.add(fn);
		return () => this.listeners.delete(fn);
	}

	envelope(recordId) {
		return this.envelopes[recordId] || null;
	}

	outcome(recordId) {
		return this.outcomes[recordId] || null;
	}

	/** The sequence number the next outcome will exceed — a submit remembers it to ignore older ones. */
	currentSeq() {
		return this.seq;
	}

	/** Tests only: forget everything and detach. */
	reset() {
		this.emitter.off("record_update_stream", this.handle);
		this.attached = false;
		this.envelopes = {};
		this.outcomes = {};
		this.listeners.clear();
		this.seq = 0;
	}
}

export const widgetEnvelopes = new WidgetEnvelopeStore();
